import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Info } from "lucide-react";

interface SpecificationFormProps {
  category: string;
  type: string;
  value: number;
  energyLabel: string;
  biobased: boolean;
  onChange: (field: string, value: any) => void;
}

export const SpecificationForm = ({ 
  category, 
  type, 
  value, 
  energyLabel, 
  biobased, 
  onChange 
}: SpecificationFormProps) => {
  
  const getUnitLabel = () => {
    if (category === 'warmtepomp') return 'kW (thermisch vermogen)';
    if (category === 'isolatie') return 'm² (oppervlakte)';
    if (category === 'zonneboiler') return 'm² (collectoroppervlak)';
    return 'Aantal';
  };

  const getValuePlaceholder = () => {
    if (category === 'warmtepomp') return 'Bijv. 5';
    if (category === 'isolatie') return 'Bijv. 50';
    if (category === 'zonneboiler') return 'Bijv. 4';
    return '1';
  };

  const showEnergyLabel = category === 'warmtepomp';
  const showBiobased = category === 'isolatie' && 
    ['spouwmuurisolatie', 'gevelisolatie', 'dakisolatie', 'vloerisolatie'].includes(type);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Specificaties van uw {category}</h3>
        <p className="text-muted-foreground mb-6">
          Vul de technische details in voor een nauwkeurige subsidieberekening.
        </p>
      </div>

      <div className="grid gap-6">
        {/* Value/Capacity Input */}
        <div className="space-y-2">
          <Label htmlFor="value">{getUnitLabel()}</Label>
          <Input
            id="value"
            type="number"
            min="0"
            step={category === 'warmtepomp' ? '0.1' : '1'}
            placeholder={getValuePlaceholder()}
            value={value || ''}
            onChange={(e) => onChange('value', parseFloat(e.target.value) || 0)}
            className="text-base"
          />
          <p className="text-sm text-muted-foreground">
            {category === 'warmtepomp' && 'Het thermisch vermogen staat meestal op het typeplaatje'}
            {category === 'isolatie' && 'Het totale oppervlakte dat geïsoleerd wordt'}
            {category === 'zonneboiler' && 'Het collectoroppervlak van de zonneboiler'}
          </p>
        </div>

        {/* Energy Label for Heat Pumps */}
        {showEnergyLabel && (
          <div className="space-y-2">
            <Label>Energielabel warmtepomp</Label>
            <Select value={energyLabel} onValueChange={(value) => onChange('energyLabel', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Selecteer energielabel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="A+++">A+++ (beste, €200 bonus)</SelectItem>
                <SelectItem value="A++">A++</SelectItem>
                <SelectItem value="A+">A+</SelectItem>
                <SelectItem value="A">A</SelectItem>
                <SelectItem value="B">B</SelectItem>
                <SelectItem value="onbekend">Onbekend/Anders</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Warmtepompen met A+++ label krijgen €200 extra subsidie
            </p>
          </div>
        )}

        {/* Biobased option for insulation */}
        {showBiobased && (
          <Card className="border-primary/20 bg-primary/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="h-4 w-4" />
                Biobased isolatiemateriaal
              </CardTitle>
              <CardDescription>
                Natuurvriendelijke isolatie krijgt extra subsidie
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="biobased"
                  checked={biobased}
                  onCheckedChange={(checked) => onChange('biobased', checked)}
                />
                <Label htmlFor="biobased">
                  Ik gebruik biobased/natuurvriendelijk isolatiemateriaal
                </Label>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Biobased isolatie krijgt een extra bonus van:
                {type === 'spouwmuurisolatie' && ' €1,50 per m²'}
                {type === 'gevelisolatie' && ' €6,00 per m²'}
                {type === 'dakisolatie' && ' €4,50 per m²'}
                {type === 'vloerisolatie' && ' €3,75 per m²'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Type-specific info cards */}
        {category === 'warmtepomp' && type === 'lucht-lucht' && (
          <Card className="border-destructive/20 bg-destructive/5">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-destructive mt-0.5" />
                <div>
                  <h4 className="font-medium text-destructive">Geen ISDE subsidie</h4>
                  <p className="text-sm text-destructive/80 mt-1">
                    Lucht-lucht warmtepompen komen niet in aanmerking voor ISDE subsidie. 
                    Wel kunt u mogelijk gebruik maken van andere regelingen.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {category === 'isolatie' && ['HR++glas', 'triple-glas'].includes(type) && (
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <h4 className="font-medium text-primary">Glasisolatie 2025</h4>
                  <p className="text-sm text-primary/80 mt-1">
                    Nieuwe regels 2025: Minimum 3 m² (was 8 m²) en u kunt 2x subsidie aanvragen binnen 24 maanden.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};